import { DEALER_ADMIN } from 'src/utils/enums/user-store-actions.enum';
import { DealerManagementService } from 'src/services/dealer-management.service';
import EditUser from 'src/views/pages/manageUser/EditUser/EditUser';
import FooterPrivacy from '../../auth/components/baseTemplate/FooterPrivacy';
import { getBrandByDealerNumber } from 'src/utils/helpers';
import { IUser } from '../../../utils/interfaces/user';
import { LoginService } from 'src/services/login.service';
import NewUser from 'src/views/pages/manageUser/NewUser/NewUser';
import Spinner from 'src/components/ui/spinner';
import Table from 'src/components/dealer-mgmt/dealer-mgmt-table';
import { useLocalization } from 'src/utils/useLocalization';
import {
    DealerAssociation,
    DealerManagementTableData,
    DealerPermission,
} from 'src/services/models/dealer-management.service';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import './styles.scss';

const DealerManagementPage = () => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isAdding, setIsAdding] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [userNameEdit, setUserNameEdit] = useState<string>('');
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [isSuccessDeactivate, setIsSuccessDeactivate] =
        useState<boolean>(false);
    const [isSuccessActivate, setIsSuccessActivate] = useState<boolean>(false);
    const [isSuccessDeleted, setIsSuccessDeleted] = useState<boolean>(false);
    const [isSuccessDeletedMFA, setIsSuccessDeletedMFA] =
        useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [usersData, setUsersData] = useState<DealerManagementTableData[]>([]);
    const [brand, setBrand] = useState<string>('vw6');
    const [dealerPermissions, setDealerPermissions] = useState<
        Array<DealerPermission>
    >([]);
    const [dealerAssociations, setDealerAssociations] = useState<
        Array<DealerAssociation>
    >([]);
    const { dealerMgmt, addNewUser, editUser, deleteUser, data } =
        useLocalization();

    const currentDealers = useSelector((state: IUser) => {
        return state.currentDealers;
    });
    const dealerDataInfo = useSelector((state: IUser) => {
        return state.dealerDataInfo;
    });
    const permissionsToken = useSelector((state: IUser) => {
        return state.permissionsToken;
    });

    async function getDealerUsersData() {
        const service = new DealerManagementService();
        const loginService = new LoginService();
        setIsLoading(true);
        try {
            const [res, tokenRes] = await Promise.all([
                service.getUsers(currentDealers?.default as string),
                loginService.getDealerProfile(permissionsToken),
                getDealerGroups(),
            ]);
            setIsLoading(false);
            if (res?.data && tokenRes?.data) {
                const { userPermissions, associations } =
                    tokenRes.data.userProfile.userData;
                setUsersData(service.mapResponse(res.data));
                setDealerPermissions(userPermissions);
                setDealerAssociations(associations);
            }
        } catch (error) {
            setIsLoading(false);
            console.error(error);
        }
    }

    const getDealerGroups = useCallback(async () => {
        const service = new DealerManagementService();

        try {
            setIsLoading(true);

            const res = await service.getDealerGroups(
                currentDealers?.default as string,
            );

            if (res?.data) {
                dispatch({
                    type: DEALER_ADMIN.SET_DEALER_GROUP_DATA,
                    payload: {
                        dealerGroupState: res.data,
                    },
                });
            }
        } catch {
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    }, [currentDealers?.default, dispatch, isAdding]);

    useEffect(() => {
        if (currentDealers && dealerDataInfo) {
            const { dealer } = dealerDataInfo;
            const dealerBrand = getBrandByDealerNumber(
                dealer.brand,
                dealer.dealerNumber,
            );
            setBrand(dealerBrand);
            getDealerUsersData();
        }
    }, [dealerDataInfo]);

    useEffect(() => {
        if (isSuccess) {
            setIsLoading(true);
            getDealerUsersData();
        }
    }, [isSuccess]);

    const handleClickItem = (userName: string) => {
        setUserNameEdit(userName);
        setIsEditing(true);
    };

    const buttonClasses = `c-btn ${brand}`;

    const handlerEditBackclick = (
        didEdit: boolean,
        shouldLoadList?: boolean,
    ) => {
        setIsEditing(didEdit);
        if (shouldLoadList) {
            setIsLoading(true);
            getDealerUsersData();
        }
    };

    function selectView() {
        if (isAdding)
            return (
                <NewUser
                    backClick={setIsAdding}
                    setSuccessNotification={setIsSuccess}
                    dealerPermissions={dealerPermissions}
                    dealerAssociations={dealerAssociations}
                    isDSMSearch={true}
                />
            );
        else if (isEditing)
            return (
                <EditUser
                    backClick={handlerEditBackclick}
                    username={userNameEdit}
                    setErrorNotification={setIsError}
                    dealerPermissions={dealerPermissions}
                    dealerAssociations={dealerAssociations}
                    setIsSuccessDeleted={setIsSuccessDeleted}
                    isDSMSearch={true}
                />
            );
        else return null;
    }
    return (
        <div>
            {selectView() || (
                <div className='user-mgmt-wrapper'>
                    {isSuccess && (
                        <div className={'success-notification'}>
                            <Notification
                                content={addNewUser.NOTIFICATION_SUCCESS}
                                type={NotificationType.SUCCESS}
                                visibility={isSuccess}
                                setVisibility={setIsSuccess}
                            />{' '}
                        </div>
                    )}
                    {isSuccessDeactivate && (
                        <div className={'success-notification'}>
                            <Notification
                                content={
                                    editUser.NOTIFICATION_SUCCESS_DEACTIVATION
                                }
                                type={NotificationType.SUCCESS}
                                visibility={isSuccessDeactivate}
                                setVisibility={setIsSuccessDeactivate}
                            />{' '}
                        </div>
                    )}
                    {isSuccessActivate && (
                        <div className={'success-notification'}>
                            <Notification
                                content={
                                    editUser.NOTIFICATION_SUCCESS_ACTIVATION
                                }
                                type={NotificationType.SUCCESS}
                                visibility={isSuccessActivate}
                                setVisibility={setIsSuccessActivate}
                            />{' '}
                        </div>
                    )}
                    {isSuccessDeleted && (
                        <div className={'success-notification'}>
                            <Notification
                                content={
                                    deleteUser.NOTIFICATION_SUCCESS_DELETION
                                }
                                type={NotificationType.SUCCESS}
                                visibility={isSuccessDeleted}
                                setVisibility={setIsSuccessDeleted}
                            />{' '}
                        </div>
                    )}
                    {isSuccessDeletedMFA && (
                        <div className={'success-notification'}>
                            <Notification
                                content={
                                    dealerMgmt.DEALER_MGMT_TABLE_USER_MESSAGES_SUCCESSMFA_DELETED
                                }
                                type={NotificationType.SUCCESS}
                                visibility={isSuccessDeletedMFA}
                                setVisibility={setIsSuccessDeletedMFA}
                            />{' '}
                        </div>
                    )}
                    {isError && (
                        <div className={'success-notification'}>
                            <Notification
                                content={editUser.NOTIFICATION_ERROR_REQUEST}
                                type={NotificationType.ERROR}
                                visibility={isError}
                                setVisibility={setIsError}
                            />{' '}
                        </div>
                    )}
                    <div className='page-title'>
                        <h5>{dealerMgmt.DEALER_MGMT_HEADER_LABEL}</h5>
                    </div>
                    <div className='page-title'>
                        <span className='address-line'>
                            {`${currentDealers?.default as string} ${
                                dealerDataInfo?.dealer.dealerName
                            } `}
                            <span className='address-line'>{`| ${usersData.length} ${dealerMgmt.DEALER_MGMT_USERS_NUMBER_LABEL}`}</span>
                        </span>

                        <button
                            className={buttonClasses}
                            onClick={() => {
                                setIsAdding(true);
                            }}
                        >
                            <span className='c-btn__text'>
                                {dealerMgmt.DEALER_MGMT_ADD_BUTTON_LABEL}
                            </span>
                        </button>
                    </div>
                    <div
                        className={`main-container ${
                            isLoading ? 'no-content' : ''
                        }`}
                    >
                        {isLoading ? (
                            <div className='loader_container'>
                                <Spinner />
                            </div>
                        ) : (
                            <>
                                {usersData.length ? (
                                    <Table
                                        items={usersData}
                                        onClick={handleClickItem}
                                        updateData={getDealerUsersData}
                                        setIsSuccessDeactivate={
                                            setIsSuccessDeactivate
                                        }
                                        setIsSuccessDeleted={
                                            setIsSuccessDeleted
                                        }
                                        setIsSuccessActivate={
                                            setIsSuccessActivate
                                        }
                                        setIsSuccessDeletedMFA={
                                            setIsSuccessDeletedMFA
                                        }
                                        setIsError={setIsError}
                                    />
                                ) : (
                                    <div className='no-content'>
                                        <div>{data.NO_DATA_AVAILABLE}</div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div style={{ gridColumnStart: 2, marginTop: '40px' }}>
                        <FooterPrivacy />
                    </div>
                </div>
            )}
        </div>
    );
};
export default DealerManagementPage;
